import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MonoLabel } from '../components/Editorial';
import Button from '../components/Button';

const AuthShell = ({ children, invite, inviteContext }) => (
  <div className={`ed-auth${invite ? ' ed-auth--invite' : ''}`}>
    <div className="ed-auth__hero">
      <div>
        <MonoLabel onDark>{invite ? 'WELCOME TO PROZPECTS' : 'SET A NEW PASSWORD'}</MonoLabel>
        <h1 className="ed-auth__hero-headline" style={{ marginTop: 16 }}>
          {invite ? (
            <>Set your<br /><em>password.</em></>
          ) : (
            <>Pick a new<br /><em>secret.</em></>
          )}
        </h1>
        {invite && inviteContext?.cohort_label && (
          <p style={{ marginTop: 18, opacity: 0.9, fontSize: 20, fontWeight: 500 }}>
            For {inviteContext.cohort_label} athletes.
          </p>
        )}
        <p className="ed-auth__hero-quote" style={{ marginTop: 24 }}>
          {invite
            ? '"You\'re already in. Pick a password and claim your profile — coaches can already see your verified numbers."'
            : '"8+ characters, mix of upper, lower, and a special — easy to remember, hard to guess."'}
        </p>
      </div>
    </div>
    <div className="ed-auth__form-wrap">
      <div className="ed-auth__form">{children}</div>
    </div>
  </div>
);

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const isInvite = searchParams.get('invite') === '1';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [inviteContext, setInviteContext] = useState(null);
  const [inviteContextError, setInviteContextError] = useState(false);
  // Android has no ProZpects app on Google Play yet — suppress the App Store
  // badge for these users so we don't dead-end them on an Apple-only link.
  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

  // For invite tokens: fetch context to (a) stamp opened_at server-side and
  // (b) personalize the headline with cohort label + masked email.
  useEffect(() => {
    if (!isInvite || !token) return;
    let cancelled = false;
    axios
      .get(`/api/auth/invite/${encodeURIComponent(token)}`)
      .then((resp) => { if (!cancelled) setInviteContext(resp.data); })
      .catch((err) => {
        if (cancelled) return;
        // 410 = the link already set a password (invite links are single use).
        setInviteContextError(err?.response?.status === 410 ? 'used' : true);
      });
    return () => { cancelled = true; };
  }, [isInvite, token]);

  // For reset tokens: record that the link was opened (admin Reset Password
  // dialog shows it) and say up front if it has already expired, instead of
  // after the user has typed a new password twice.
  useEffect(() => {
    if (isInvite || !token) return;
    let cancelled = false;
    axios
      .post('/api/auth/reset-password/opened', { token })
      .then((resp) => {
        if (cancelled) return;
        if (resp.data?.status === 'expired' && resp.data?.kind === 'setup') {
          setError('This setup link has expired. Setup links work for 30 days.');
        } else if (resp.data?.status === 'expired') {
          setError('This reset link has expired. Reset links work for 1 hour.');
        } else if (resp.data?.status === 'used') {
          setError('This link has already been used to set a password. Sign in, or request a new reset link.');
        } else if (resp.data?.status === 'invalid') {
          setError('This reset link is invalid.');
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isInvite, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', {
        token,
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may be invalid or expired.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <AuthShell invite={isInvite} inviteContext={inviteContext}>
        <div className="auth-logo" style={{ marginBottom: 24 }}>
          <img src="/logo-wordmark.png" alt="ProZpects" style={{ width: 320, maxWidth: '100%', height: 'auto', display: 'block' }} />
        </div>
        <div className="auth-error" role="alert">
          {isInvite ? 'Invalid invite link. No token provided.' : 'Invalid reset link. No token provided.'}
        </div>
        <div className="auth-footer">
          <Link to="/forgot-password">Request a new reset link</Link>
        </div>
      </AuthShell>
    );
  }

  if (isInvite && inviteContextError) {
    return (
      <AuthShell invite>
        <div className="auth-logo" style={{ marginBottom: 24 }}>
          <img src="/logo-wordmark.png" alt="ProZpects" style={{ width: 320, maxWidth: '100%', height: 'auto', display: 'block' }} />
        </div>
        {inviteContextError === 'used' ? (
          <>
            <div className="auth-error" role="alert">
              This invite link has already been used to set a password.
            </div>
            <div className="auth-footer">
              <Link to="/login">Sign in</Link>
              {' · '}
              <Link to="/forgot-password">Request a new reset link</Link>
            </div>
          </>
        ) : (
          <>
            <div className="auth-error" role="alert">
              This invite link is invalid or has expired.
            </div>
            <div className="auth-footer">
              <Link to="/forgot-password">Request a new reset link</Link>
            </div>
          </>
        )}
      </AuthShell>
    );
  }

  const eyebrow = isInvite ? 'SET YOUR PASSWORD' : 'RESET PASSWORD';
  const formHeadline = isInvite ? 'Welcome.' : 'New password.';

  return (
    <AuthShell invite={isInvite} inviteContext={inviteContext}>
      <div className="auth-logo" style={{ marginBottom: 24 }}>
        <img src="/logo-wordmark.png" alt="ProZpects" style={{ width: 320, maxWidth: '100%', height: 'auto', display: 'block' }} />
      </div>
      <MonoLabel>{eyebrow}</MonoLabel>
      <h2 style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 36, fontWeight: 400, letterSpacing: '-0.015em', margin: '8px 0 24px' }}>
        {formHeadline}
      </h2>
      {isInvite && inviteContext?.email_masked && (
        <p style={{ marginTop: -12, marginBottom: 24, color: '#64748b', fontSize: 14 }}>
          Setting password for <strong>{inviteContext.email_masked}</strong>
        </p>
      )}

        {success ? (
          <div className="auth-success" role="alert">
            {isInvite ? "You're set. Redirecting to login..." : 'Password reset successful! Redirecting to login...'}
          </div>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit}>
            {error && (
              <div className="auth-error" role="alert">
                {error}
                {(error.includes('expired') || error.includes('invalid') || error.includes('Invalid') || error.includes('already been used')) && (
                  <div style={{ marginTop: '8px' }}>
                    <Link to="/forgot-password">Request a new reset link</Link>
                  </div>
                )}
              </div>
            )}

            <div className="auth-field">
              <label htmlFor="reset-password">New Password</label>
              <div className="auth-password-wrapper">
                <input
                  id="reset-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  aria-describedby="reset-password-hint"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              </div>
              <div className="auth-password-hint" id="reset-password-hint">
                Password must be at least 8 characters and include an uppercase letter, a lowercase letter, and a special character (!@#$%^&*).
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="reset-confirm-password">Confirm New Password</label>
              <div className="auth-password-wrapper">
                <input
                  id="reset-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </Button>
              </div>
            </div>

            <Button type="submit" variant="primary" className="auth-btn" disabled={loading}>
              {loading
                ? (isInvite ? 'Setting...' : 'Resetting...')
                : (isInvite ? 'Set Password' : 'Reset Password')}
            </Button>
          </form>
        )}

      {isInvite && !isAndroid && (
        <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
          <MonoLabel>GET THE APP</MonoLabel>
          <p style={{ fontSize: 14, color: '#475569', margin: '8px 0 16px' }}>
            ProZpects works best on iPhone. Download to message coaches, build your trading card, and add photos and video.
          </p>
          <a
            href="https://apps.apple.com/us/app/prozpects/id6759935775"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download ProZpects on the App Store"
            style={{ display: 'inline-block' }}
          >
            <img
              src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
              alt="Download on the App Store"
              style={{ height: 50, width: 'auto', display: 'block' }}
            />
          </a>
        </div>
      )}

      <div className="auth-footer">
        <Link to="/login">Back to login</Link>
      </div>
    </AuthShell>
  );
}
